import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { FaUser, FaShieldAlt, FaSave, FaLock, FaEye, FaEyeSlash, FaCropAlt, FaTimes, FaSpinner, FaUserCircle, FaCog, FaDownload, FaDesktop, FaMobileAlt, FaSignOutAlt } from 'react-icons/fa';
import { FiArrowLeft } from 'react-icons/fi';
import Swal from 'sweetalert2';
import 'sweetalert2/dist/sweetalert2.min.css';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';

import { API_BASE_URL } from '../../config'; 
import api from '../../api/axiosConfig'; 

import styles from './Perfil.module.css';
import { generateProfileReport } from './generateProfileReport';
import PageHeader from '../../components/PageHeader/PageHeader';
import LoadingOverlay from '../../components/LoadingOverlay/LoadingOverlay';

function getCroppedImg(image, crop, fileName) {
    const canvas = document.createElement('canvas');
    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;
    canvas.width = crop.width;
    canvas.height = crop.height;
    const ctx = canvas.getContext('2d');
    const pixelRatio = window.devicePixelRatio;
    canvas.width = crop.width * pixelRatio;
    canvas.height = crop.height * pixelRatio;
    ctx.setTransform(pixelRatio, 0, 0, pixelRatio, 0, 0);
    ctx.imageSmoothingQuality = 'high';
    ctx.drawImage(image, crop.x * scaleX, crop.y * scaleY, crop.width * scaleX, crop.height * scaleY, 0, 0, crop.width, crop.height);
    return new Promise((resolve, reject) => {
        canvas.toBlob((blob) => {
            if (!blob) { reject(new Error('Canvas is empty')); return; }
            blob.name = fileName;
            resolve(blob);
        }, 'image/jpeg', 1);
    });
}

const Perfil = () => {
    const [userData, setUserData] = useState({ username: '' });
    const [passwordData, setPasswordData] = useState({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
    const [imageSrc, setImageSrc] = useState('');
    const [crop, setCrop] = useState();
    const [completedCrop, setCompletedCrop] = useState(null);
    const [showCropModal, setShowCropModal] = useState(false);
    const [croppedImageBlob, setCroppedImageBlob] = useState(null);
    const [previewSource, setPreviewSource] = useState('');
    const [loading, setLoading] = useState(true);
    const [pwdShown, setPwdShown] = useState({ current: false, new: false, confirm: false });
    const [isInfoSaving, setIsInfoSaving] = useState(false);
    const [isPasswordSaving, setIsPasswordSaving] = useState(false);
    const [passwordStrength, setPasswordStrength] = useState({ score: 0, label: 'Muito Fraca', color: '#ef4444' });
    const [pageVisible, setPageVisible] = useState(false);
    const [activeTab, setActiveTab] = useState('pessoal');
    const [sessions, setSessions] = useState([]);
    const [activities, setActivities] = useState([]);

    const fileInputRef = useRef(null);
    const imgRef = useRef(null);

    useEffect(() => {
        document.body.setAttribute('data-theme', 'light');
        const MIN_LOADING_TIME = 200;
        const startTime = Date.now();

        const fetchAllData = async () => {
            try {
                const [userRes, sessionsRes] = await Promise.all([
                    api.get('/perfil'),
                    api.get('/perfil/sessoes')
                ]);

                const userData = userRes.data;
                setUserData({ username: userData.username });
                if (userData.foto_perfil_url) {
                    setPreviewSource(`${API_BASE_URL}${userData.foto_perfil_url}`);
                }

                const sessionsData = sessionsRes.data;
                setSessions(sessionsData);

                const lastLoginTime = localStorage.getItem('lastLoginTime');
                const initialActivities = [];
                if (lastLoginTime) {
                    initialActivities.push({
                        id: 1,
                        icon: FaUser,
                        title: 'Último login realizado',
                        timestamp: new Date(lastLoginTime).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
                    });
                }
                setActivities(initialActivities);

            } catch (err) {
                if (err.response && err.response.status !== 401) {
                    Swal.fire({ 
                        icon: 'error', 
                        title: 'Erro', 
                        text: err.response?.data?.error || err.message, 
                        confirmButtonColor: '#16a34a' 
                    });
                }
            } finally {
                const endTime = Date.now();
                const duration = endTime - startTime;
                const remainingTime = MIN_LOADING_TIME - duration;

                const finalLoadingSet = () => {
                    setLoading(false);
                    setTimeout(() => setPageVisible(true), 100);
                };

                if (remainingTime > 0) {
                    setTimeout(finalLoadingSet, remainingTime);
                } else {
                    finalLoadingSet();
                }
            }
        };

        fetchAllData();
    }, []);

    const checkPasswordStrength = (password) => {
        let score = 0;
        let label = 'Muito Fraca';
        let color = '#ef4444';
        if (password.length >= 8) score++;
        if (/[a-z]/.test(password) && /[A-Z]/.test(password)) score++;
        if (/[0-9]/.test(password)) score++;
        if (/[^A-Za-z0-9]/.test(password)) score++;
        switch (score) {
            case 1: label = 'Fraca'; color = '#f97316'; break;
            case 2: label = 'Razoável'; color = '#facc15'; break;
            case 3: label = 'Boa'; color = '#84cc16'; break;
            case 4: label = 'Forte'; color = '#22c55e'; break;
            default: break;
        }
        setPasswordStrength({ score, label, color });
    };

    const handlePasswordChange = (e) => {
        const { name, value } = e.target;
        setPasswordData({ ...passwordData, [name]: value });
        if (name === 'newPassword') checkPasswordStrength(value);
    };

    const handleInfoSubmit = async (e) => {
        e.preventDefault();
        setIsInfoSaving(true);
        const formData = new FormData();
        formData.append('username', userData.username);
        if (croppedImageBlob) formData.append('foto_perfil', croppedImageBlob);
        try {
            const response = await api.put('/perfil/dados', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data',
                },
            });
            
            const result = response.data;
            if (!result) throw new Error('Falha ao atualizar perfil.');

            Swal.fire({ icon: 'success', title: 'Sucesso!', text: result.message, timer: 2000, showConfirmButton: false });
            setCroppedImageBlob(null);
            if (result.newImageUrl) {
                localStorage.setItem('foto_perfil_url', result.newImageUrl);
                window.dispatchEvent(new Event('storage'));
            }
        } catch (err) {
            if (err.response && err.response.status !== 401) {
                Swal.fire({ icon: 'error', title: 'Oops...', text: err.response?.data?.error || err.message, confirmButtonColor: '#16a34a' });
            }
        } finally {
            setIsInfoSaving(false);
        }
    };

    const handlePasswordSubmit = async (e) => {
        e.preventDefault();
        if (passwordData.newPassword !== passwordData.confirmNewPassword) {
            Swal.fire({ icon: 'warning', title: 'Atenção', text: 'A nova senha e a confirmação não coincidem.', confirmButtonColor: '#16a34a' });
            return;
        }
        setIsPasswordSaving(true);
        try {
            const response = await api.put('/perfil/senha', passwordData);
            
            const result = response.data;
            if (!result) throw new Error('Falha ao atualizar senha.');

            Swal.fire({ icon: 'success', title: 'Sucesso!', text: result.message, timer: 2000, showConfirmButton: false });
            
            const newActivity = {
                id: Date.now(),
                icon: FaLock,
                title: 'Senha alterada com sucesso',
                timestamp: new Date().toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
            };
            setActivities(prevActivities => [newActivity, ...prevActivities]);

            setPasswordData({ currentPassword: '', newPassword: '', confirmNewPassword: '' });
            setPasswordStrength({ score: 0, label: 'Muito Fraca', color: '#ef4444' });
        } catch (err) {
            if (err.response && err.response.status !== 401) {
                Swal.fire({ icon: 'error', title: 'Oops...', text: err.response?.data?.error || err.message, confirmButtonColor: '#16a34a' });
            }
        } finally {
            setIsPasswordSaving(false);
        }
    };

    const handleExportData = () => {
        generateProfileReport(userData);
    };

    const handleDisconnectSession = (sessionId, sessionName) => {
        Swal.fire({
            title: 'Desconectar Sessão?',
            text: `Você tem certeza que deseja desconectar a sessão em "${sessionName}"?`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonColor: '#16a34a',
            cancelButtonColor: '#d33',
            confirmButtonText: 'Sim, desconectar',
            cancelButtonText: 'Cancelar'
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await api.delete(`/perfil/sessoes/${sessionId}`);
                    setSessions(currentSessions => currentSessions.filter(s => s.id !== sessionId));
                    Swal.fire({
                        title: 'Desconectado!',
                        text: `A sessão em ${sessionName} foi encerrada.`,
                        icon: 'success',
                        confirmButtonColor: '#16a34a'
                    });
                } catch (err) {
                    if (err.response && err.response.status !== 401) {
                        Swal.fire('Erro', err.response?.data?.error || err.message, 'error');
                    }
                }
            }
        });
    };
    
    const handleFileChange = (e) => { if (e.target.files && e.target.files.length > 0) { setCrop(undefined); const reader = new FileReader(); reader.addEventListener('load', () => { setImageSrc(reader.result?.toString() || ''); setShowCropModal(true); }); reader.readAsDataURL(e.target.files[0]); } };
    const onImageLoad = (e) => { const { width, height } = e.currentTarget; const crop = centerCrop(makeAspectCrop({ unit: '%', width: 90 }, 1, width, height), width, height); setCrop(crop); };
    const handleCropSave = async () => { if (!completedCrop || !imgRef.current) return; const croppedBlob = await getCroppedImg(imgRef.current, completedCrop, 'profile.jpg'); setCroppedImageBlob(croppedBlob); setPreviewSource(URL.createObjectURL(croppedBlob)); setShowCropModal(false); setImageSrc(''); };
    const togglePasswordVisibility = (field) => setPwdShown(prev => ({ ...prev, [field]: !prev[field] }));

    return (
        <>
            {loading && <LoadingOverlay text="Carregando perfil..." />}

            <div className={`${styles.pageWrapper} ${pageVisible ? styles.visible : ''}`}>
                <div className={styles.container}>
                    <PageHeader 
                        title="Meu Perfil"
                        subtitle="Gerencie suas informações pessoais, segurança e preferências da conta."
                        backTo="/"
                    />
                    
                    <div className={styles.profileLayout}>
                        <nav className={styles.profileNav}>
                            <button className={`${styles.navButton} ${activeTab === 'pessoal' ? styles.active : ''}`} onClick={() => setActiveTab('pessoal')}>
                                <FaUser /> <span>Informações</span>
                            </button>
                            <button className={`${styles.navButton} ${activeTab === 'seguranca' ? styles.active : ''}`} onClick={() => setActiveTab('seguranca')}>
                                <FaShieldAlt /> <span>Segurança</span>
                            </button>
                            <button className={`${styles.navButton} ${activeTab === 'avancado' ? styles.active : ''}`} onClick={() => setActiveTab('avancado')}>
                                <FaCog /> <span>Avançado</span>
                            </button>
                        </nav>

                        <main className={styles.profileContent}>
                            {activeTab === 'pessoal' && (
                                <div className={styles.contentSection} key="pessoal">
                                    <div className={styles.formContainer}>
                                        <h2>Informações Pessoais</h2>
                                        <form onSubmit={handleInfoSubmit}>
                                            <div className={styles.avatarUploadSection}>
                                                <div className={styles.avatarWrapper} onClick={() => fileInputRef.current.click()}>
                                                    {previewSource ? <img src={previewSource} alt="Preview" className={styles.avatarPreview} /> : <FaUserCircle className={styles.avatarPreviewPlaceholder} />}
                                                    <div className={styles.avatarOverlay}>Trocar Foto</div>
                                                </div>
                                                <input type="file" accept="image/*" onChange={handleFileChange} ref={fileInputRef} className={styles.fileInput} />
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label htmlFor="username">Nome de Usuário</label>
                                                <div className={styles.inputWrapper}>
                                                    <FaUser className={styles.inputIcon} />
                                                    <input type="text" id="username" name="username" value={userData.username} onChange={(e) => setUserData({ ...userData, [e.target.name]: e.target.value })} required />
                                                </div>
                                            </div>
                                            <button type="submit" className={styles.btnPrimary} disabled={isInfoSaving}>
                                                {isInfoSaving ? <FaSpinner className={styles.spinner} /> : <><FaSave /> Salvar Alterações</>}
                                            </button>
                                        </form>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'seguranca' && (
                                <div className={styles.contentSection} key="seguranca">
                                    <div className={styles.formContainer}>
                                        <h2>Alterar Senha</h2>
                                        <form onSubmit={handlePasswordSubmit}>
                                            <div className={styles.formGroup}>
                                                <label htmlFor="currentPassword">Senha Atual</label>
                                                <div className={styles.inputWrapper}>
                                                    <FaLock className={styles.inputIcon} />
                                                    <input type={pwdShown.current ? 'text' : 'password'} id="currentPassword" name="currentPassword" value={passwordData.currentPassword} onChange={handlePasswordChange} required />
                                                    <span className={styles.passwordToggle} onClick={() => togglePasswordVisibility('current')}>{pwdShown.current ? <FaEyeSlash /> : <FaEye />}</span>
                                                </div>
                                            </div>
                                            <div className={styles.formGroup}>
                                                <label htmlFor="newPassword">Nova Senha</label>
                                                <div className={styles.inputWrapper}>
                                                    <FaLock className={styles.inputIcon} />
                                                    <input type={pwdShown.new ? 'text' : 'password'} id="newPassword" name="newPassword" value={passwordData.newPassword} onChange={handlePasswordChange} required />
                                                    <span className={styles.passwordToggle} onClick={() => togglePasswordVisibility('new')}>{pwdShown.new ? <FaEyeSlash /> : <FaEye />}</span>
                                                </div>
                                            </div>
                                            {passwordData.newPassword && (
                                                <div className={styles.passwordStrengthMeter}>
                                                    <div className={styles.strengthBarWrapper}>
                                                        {[1, 2, 3, 4].map(i => <div key={i} className={styles.strengthBar} style={{ backgroundColor: passwordStrength.score >= i ? passwordStrength.color : '' }}></div>)}
                                                    </div>
                                                    <span className={styles.strengthLabel} style={{ color: passwordStrength.color }}>{passwordStrength.label}</span>
                                                </div>
                                            )}
                                            <div className={styles.formGroup}>
                                                <label htmlFor="confirmNewPassword">Confirmar Nova Senha</label>
                                                <div className={styles.inputWrapper}>
                                                    <FaLock className={styles.inputIcon} />
                                                    <input type={pwdShown.confirm ? 'text' : 'password'} id="confirmNewPassword" name="confirmNewPassword" value={passwordData.confirmNewPassword} onChange={handlePasswordChange} required />
                                                    <span className={styles.passwordToggle} onClick={() => togglePasswordVisibility('confirm')}>{pwdShown.confirm ? <FaEyeSlash /> : <FaEye />}</span>
                                                </div>
                                            </div>
                                            <button type="submit" className={styles.btnPrimary} disabled={isPasswordSaving}>
                                                {isPasswordSaving ? <FaSpinner className={styles.spinner} /> : <><FaLock /> Alterar Senha</>}
                                            </button>
                                        </form>
                                    </div>
                                    
                                    <div>
                                        <h2>Atividade Recente</h2>
                                        <ul className={styles.activityLog}>
                                            {activities.map(activity => {
                                                const IconComponent = activity.icon;
                                                return (
                                                    <li key={activity.id}>
                                                        <IconComponent className={styles.activityIcon} />
                                                        <div>
                                                            <strong>{activity.title}</strong>
                                                            <span>{activity.timestamp}</span>
                                                        </div>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </div>
                                </div>
                            )}

                            {activeTab === 'avancado' && (
                                <div className={styles.contentSection} key="avancado">
                                    <div>
                                        <h2>Exportar Dados</h2>
                                        <p className={styles.sectionDescription}>
                                            Você pode solicitar uma exportação de todos os seus dados. Um arquivo PDF será gerado para download.
                                        </p>
                                        <button className={`${styles.btnSecondary} ${styles.btnAutoWidth}`} onClick={handleExportData}>
                                            <FaDownload /> Exportar Meus Dados
                                        </button>
                                    </div>

                                    <div>
                                        <h2>Sessões Ativas</h2>
                                        <p className={styles.sectionDescription}>
                                            Esta é uma lista de dispositivos que acessaram sua conta. Desconecte qualquer sessão que você não reconheça.
                                        </p>
                                        <ul className={styles.sessionsList}>
                                            {sessions.map(session => (
                                                <li key={session.id} className={`${styles.sessionItem} ${session.isCurrent ? styles.isCurrent : ''}`}>
                                                    {session.deviceName.toLowerCase().includes('windows') || session.deviceName.toLowerCase().includes('mac') ?
                                                        <FaDesktop className={styles.sessionIcon} /> :
                                                        <FaMobileAlt className={styles.sessionIcon} />
                                                    }
                                                    <div className={styles.sessionDetails}>
                                                        <strong>{session.deviceName}</strong>
                                                        <span className={styles.sessionMeta}>
                                                            {session.location} - Última atividade: {new Date(session.lastActive).toLocaleString('pt-BR', {dateStyle: 'short', timeStyle: 'short'})}
                                                        </span>
                                                    </div>
                                                    
                                                    {session.isCurrent ? (
                                                        <button className={`${styles.btnSecondary} ${styles.currentSessionBtn}`} disabled>Sessão Atual</button>
                                                    ) : (
                                                        <button className={styles.sessionActionBtn} onClick={() => handleDisconnectSession(session.id, session.deviceName)}>
                                                            <FaSignOutAlt /> Desconectar
                                                        </button>
                                                    )}
                                                </li>
                                            ))}
                                        </ul>
                                    </div>
                                </div>
                            )}
                        </main>
                    </div>

                    {showCropModal && (
                        <div className={styles.cropModalOverlay}>
                            <div className={styles.cropModalContent}>
                                <div className={styles.cropModalHeader}>
                                    <h3>Ajustar Foto de Perfil</h3>
                                    <button onClick={() => setShowCropModal(false)} className={styles.closeModalBtn}><FaTimes /></button>
                                </div>
                                <div className={styles.cropContainer}>
                                <ReactCrop crop={crop} onChange={(_, p) => setCrop(p)} onComplete={(c) => setCompletedCrop(c)} aspect={1} circularCrop>
                                        <img ref={imgRef} alt="Crop" src={imageSrc} onLoad={onImageLoad} style={{ maxHeight: '60vh' }} />
                                    </ReactCrop>
                                </div>
                                <div className={styles.cropModalFooter}>
                                    <button onClick={() => setShowCropModal(false)} className={styles.btnSecondary}>Cancelar</button>
                                    <button onClick={handleCropSave} className={styles.btnPrimary}><FaCropAlt /> Aplicar Corte</button>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </>
    );
};

export default Perfil;